import { useEffect, useState } from "react";
import { loadThumbnail } from "@/features/coach-review/hooks/useThumbnail";
import type { CoachingPlaylist } from "@/lib/types";

/**
 * Resolve a playlist's cover-thumbnail note id by walking
 * `playlist.items[]` until one returns a non-null thumbnail URL.
 *
 * Ports the legacy `_resolveFeedbackPlaylistCover`
 * (`js/coaching/feedback.js`). The standalone note-thumbnail
 * endpoint deliberately does NOT honor the
 * playlist-grants-access rule (per CLAUDE.md), so a private item
 * returns null and we fall through to the next item. If every
 * item fails, returns `null` and the caller renders a placeholder
 * cover — which is the correct "no cover available" state.
 *
 * Returns the resolved cover note id (number) or `null` while
 * walking / on total failure. Cached + de-duped via the shared
 * `loadThumbnail` module, so a sibling `<FeedbackThumb>` mounting
 * the same id later just reads the cache.
 *
 * Sequential rather than `Promise.all`: stop fetching as soon as
 * a cover is found, and avoid racing multiple winners. Bails on
 * unmount via a `cancelled` flag.
 */
export function usePlaylistCover(
  playlist: CoachingPlaylist,
): number | null {
  const items = playlist.items ?? [];
  // Walk identity is the ordered list of item ids — refetch only
  // when the order or membership changes.
  const itemIdsKey = items.map((n) => n.id).join(",");
  const [coverId, setCoverId] = useState<number | null>(null);

  useEffect(() => {
    let cancelled = false;
    setCoverId(null);
    if (items.length === 0) return;
    (async () => {
      for (const note of items) {
        if (cancelled) return;
        const url = await loadThumbnail("note", note.id);
        if (cancelled) return;
        if (url) {
          setCoverId(note.id);
          return;
        }
      }
      // Every item failed — leave coverId at null (placeholder).
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemIdsKey]);

  return coverId;
}
